import { ICertification, ISimulation } from '@/types';

interface Params {
  /** The current simulation to find the next simulation for */
  simulation: ISimulation | null;
  /** The certification containing the current simulation */
  certification: ICertification | null;
}

/**
 * Finds the next simulation in the sequence based on the current simulation and the certifications data.
 *
 * The logic for determining the next simulation is as follows:
 * 1. We first combine all simulations from the certification into a single array. This includes:
 *   - The role-play simulations (certification.simulations)
 *  - The certification practice (certification.certificationPractice)
 *  - The certification simulation (certification.certificationSimulation)
 * 2. We then find the index of the current simulation within this combined array.
 * 3. If the current simulation is not found or is the last one in the array, 
 *    we return undefined (indicating there is no next simulation).
 * 4. If there is a next simulation, we return it.
 *
 * @param params - An object containing the current simulation and the parent certification
 * @returns The next simulation in the sequence, or undefined if there is no next simulation
 */
export function getNextSimulation({ simulation, certification }: Params): ISimulation | undefined {
  // If there is no current simulation or certification, we cannot find a next simulation
  if (!simulation || !certification) return undefined;

  // Combine all simulations from the certification into a single array for easier processing
  // This includes the role-play simulations, the certification practice, 
  // and the certification simulation (if they exist)
  const allCertSimulations = [
    ...(certification.simulations || []),
    ...(certification.certificationPractice ? [certification.certificationPractice] : []),
    ...(certification.certificationSimulation ? [certification.certificationSimulation] : []),
  ];

  // Find the index of the current simulation within the certification's simulations
  const currentIndex = allCertSimulations.findIndex((s) => {
    return s.id === simulation.id;
  });

  // If the current simulation is not found or is the last one in the list, return undefined
  if (currentIndex === -1 || currentIndex + 1 >= allCertSimulations.length) {
    return undefined;
  }

  // Return the next simulation in the certification's simulations array
  const nextSimulation = allCertSimulations[currentIndex + 1];

  // If for some reason the next simulation is not found, return undefined
  if (!nextSimulation) return undefined;

  // Return the next simulation
  return nextSimulation;
}
